import { AppConfig } from "@/config";
import { EnvVariables } from "@/env-variables";
import { envStorage } from "@/lib/storage/envIsolatedStorage";
import {
  type ChargeCodeApi,
  honouredBrowserChoice,
  implementationFor,
  resolveChargeCodeApi,
} from "./charge-code-api.rules";

export type { ChargeCodeApi } from "./charge-code-api.rules";
export { namingChargeCodeApi } from "./charge-code-api.rules";

const CHOSEN_CHARGE_CODE_API_KEY = "charge_code_api";

/** Shown where the Debug Panel is, the one place a user makes the choice. */
const switchShown = AppConfig.enableDebugMode || AppConfig.enableDevTools;

/** Read once, when the app loads, so every screen and request speaks one version until the next load. */
const choiceAtLoad = honouredBrowserChoice(
  envStorage.getItem(CHOSEN_CHARGE_CODE_API_KEY),
  switchShown,
);

/**
 * The charge-code API this UI talks to service-management-service with: the version the user
 * chose in this browser when the app loaded (only where the Debug Panel's switch is shown), else
 * `VITE_CHARGE_CODE_API`, else v1. The server serves v1 and v2 at the same time; this only selects
 * which one this UI uses, and every part of the UI that depends on the version asks here.
 */
export const chargeCodeApi = (): ChargeCodeApi =>
  resolveChargeCodeApi(choiceAtLoad, EnvVariables.chargeCodeApi);

/**
 * Stores the user's choice in this browser. The UI reads it when the app loads, so the caller
 * reloads the page for the whole UI to speak the new version at once.
 */
export const chooseChargeCodeApi = (api: ChargeCodeApi): void => {
  envStorage.setItem(CHOSEN_CHARGE_CODE_API_KEY, api);
};

/**
 * The V1 or the V2 implementation, for the charge-code API this UI speaks. Used once, where a screen
 * with both versions is mounted, so neither version ever asks which API the UI speaks.
 */
export const forChargeCodeApi = <T>(implementations: Record<ChargeCodeApi, T>): T =>
  implementationFor(implementations, chargeCodeApi());
